import { Controller } from "@hotwired/stimulus"
import { startIcons } from "../import_lucid"
import * as echarts from 'echarts'

// Loads pipeline result HTML inline within the message bubble
export default class extends Controller {
  static values = {
    url: String,
    sql: String,
    meta: String,
    followUps: Array
  }

  connect() {
    if (this.urlValue) {
      this.loadResult()
    }
  }

  disconnect() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
    }
  }

  loadResult() {
    const container = this.element
    const resultContent = container.querySelector('.result-content')
    const sqlSection = container.querySelector('.sql-section')
    const sqlContent = sqlSection?.querySelector('[data-sql-toggle-target="content"]')
    const followUpsSection = container.querySelector('.follow-ups-section')
    const followUpButtons = followUpsSection?.querySelector('.laz-suggest')

    if (!resultContent) return

    fetch(this.urlValue)
      .then(response => response.text())
      .then(html => {
        resultContent.innerHTML = html
        startIcons(resultContent)

        // Wire up copy & download buttons
        this._initCardButtons(resultContent)

        // Resize AI charts dynamically on window resize
        this._initChartResize(resultContent)

        // Show SQL section if SQL is available
        if (sqlSection && this.sqlValue) {
          sqlSection.classList.remove('hidden')
          if (sqlContent) sqlContent.textContent = this.sqlValue
        }

        // Show follow-up suggestions
        if (followUpsSection && followUpButtons && this.followUpsValue && this.followUpsValue.length > 0) {
          followUpButtons.innerHTML = ''
          // Rotate through chip icons
          const chipIcons = [
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-6 4 3 5-7"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18M12 3l9 9-9 9"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h18M6 12h12M10 17h4"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>'
          ]
          this.followUpsValue.forEach((text, i) => {
            const btn = document.createElement('button')
            btn.className = 'laz-chip'
            btn.innerHTML = chipIcons[i % chipIcons.length] + ' ' + this._escapeHtml(text)
            btn.addEventListener('click', () => {
              const input = document.getElementById('chat-input')
              if (input) {
                input.value = text
                input.focus()
              }
            })
            followUpButtons.appendChild(btn)
          })
          followUpsSection.classList.remove('hidden')
        }

        // Hide suggestions area after first result
        const suggestionsArea = document.getElementById('suggestions-area')
        if (suggestionsArea) suggestionsArea.remove()
      })
      .catch(err => {
        resultContent.innerHTML = '<div class="p-4 text-sm text-red-500">Failed to load results</div>'
        console.error('Pipeline result load error:', err)
      })
  }

  _initCardButtons(container) {
    // Copy button — copies chart as PNG image, or table data as tab-separated text
    container.querySelectorAll('.laz-copy-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const card = btn.closest('.laz-card')
        if (!card) return

        // Chart card — copy as PNG image
        const chartEl = card.querySelector('[data-controller="apache-echarts"]')
        if (chartEl) {
          const instance = echarts.getInstanceByDom(chartEl)
          if (instance) {
            const dataUrl = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
            fetch(dataUrl)
              .then(res => res.blob())
              .then(blob => navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]))
              .catch(() => {})
            this._showCheckmark(btn, 'Copied!', 'Copy chart')
            return
          }
        }

        // Table or stat — copy as text
        const tbl = card.querySelector('table')
        let text = ''
        if (tbl) {
          const rows = []
          tbl.querySelectorAll('tr').forEach(r => {
            const cells = []
            r.querySelectorAll('th, td').forEach(c => cells.push(c.textContent.trim()))
            rows.push(cells.join('\t'))
          })
          text = rows.join('\n')
        } else {
          const val = card.querySelector('.laz-stat-value')
          text = val ? val.textContent.trim() : ''
        }
        navigator.clipboard.writeText(text).catch(() => {})
        this._showCheckmark(btn, 'Copied!', 'Copy data')
      })
    })

    // Download button — downloads table data as CSV
    container.querySelectorAll('.laz-download-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const card = btn.closest('.laz-card')
        if (!card) return
        const tbl = card.querySelector('table')
        if (!tbl) return
        const rows = []
        tbl.querySelectorAll('tr').forEach(r => {
          const cells = []
          r.querySelectorAll('th, td').forEach(c => {
            cells.push('"' + c.textContent.trim().replace(/"/g, '""') + '"')
          })
          rows.push(cells.join(','))
        })
        const blob = new Blob([rows.join('\n')], { type: 'text/csv' })
        const a = document.createElement('a')
        const objUrl = URL.createObjectURL(blob)
        a.href = objUrl
        a.download = 'data.csv'
        a.click()
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000)
        this._showCheckmark(btn, 'Downloaded!', 'Download CSV')
      })
    })

    // Excel button — downloads table data as XLSX via server
    container.querySelectorAll('.laz-excel-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!this.urlValue) return
        const urlObj = new URL(this.urlValue, window.location.origin)
        const url = urlObj.pathname + '/export_excel' + urlObj.search
        window.location.href = url
        this._showCheckmark(btn, 'Downloading...', 'Download Excel', 2000)
      })
    })
  }

  _showCheckmark(btn, tempTitle, restoreTitle, duration = 1500) {
    const orig = btn.innerHTML
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px;color:#10b981"><path d="M20 6 9 17l-5-5"/></svg>'
    btn.title = tempTitle
    setTimeout(() => { btn.innerHTML = orig; btn.title = restoreTitle }, duration)
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Resize only AI chat charts on window resize
  _initChartResize(container) {
    const chartEls = container.querySelectorAll('[data-controller="apache-echarts"]')
    if (chartEls.length === 0) return

    this._resizeHandler = () => {
      chartEls.forEach(el => {
        const instance = echarts.getInstanceByDom(el)
        if (instance) instance.resize()
      })
    }
    window.addEventListener('resize', this._resizeHandler)
  }
}
